import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';

import type { DatabaseEntities, Maybe, YardTaskAttachment } from '@nova/core';
import { documentKeys } from '@satellite/composables/queryKeys';
import documentService from '@satellite/services/document-service';

type UploadAttachmentVariables = {
  relatedEntity: DatabaseEntities;
  relatedId: string;
  file: File;
};

export interface UseAttachmentUploadOptions {
  /** Which entity the documents hang off, e.g. `DatabaseEntities.YardTask`. */
  relatedEntity: MaybeRefOrGetter<DatabaseEntities>;
  /** The entity's id. Nothing is uploaded until there is one. */
  relatedId: MaybeRefOrGetter<Maybe<string>>;
  /** Runs once the upload succeeds, before the list is invalidated. */
  onSuccess?: (attachment: YardTaskAttachment) => void;
  /** Runs when the upload fails. `uploadAttachment` still resolves. */
  onError?: (error: unknown) => void;
}

/**
 * Adds a document to an entity's attachments and invalidates the list it belongs to, so every
 * page comes back from the API rather than being patched locally. Accepts any file: which of
 * them an app is willing to attach is `getAttachmentRejection`'s call, before this is reached.
 * Pairs with `useAttachmentList` and `useAttachmentDelete`.
 */
export function useAttachmentUpload({
  relatedEntity,
  relatedId,
  onSuccess,
  onError
}: UseAttachmentUploadOptions) {
  const queryClient = useQueryClient();

  const mutation = useMutation<YardTaskAttachment, unknown, UploadAttachmentVariables>({
    mutationFn: ({ relatedEntity: entity, relatedId: id, file }) =>
      documentService.uploadDocument(entity, id, file),
    onSuccess: async (attachment, variables) => {
      onSuccess?.(attachment);

      await queryClient.invalidateQueries({
        queryKey: documentKeys.list(variables.relatedEntity, variables.relatedId)
      });
    },
    onError
  });

  /**
   * Never rejects: failures reach the caller through `onError`.
   *
   * Both the entity and its id are read here rather than on the way back, so a list that moved
   * on while the request was in flight is not the one invalidated.
   */
  async function uploadAttachment(file: Maybe<File>): Promise<void> {
    const currentEntity = toValue(relatedEntity);
    const currentId = toValue(relatedId);
    if (!currentId || !file) {
      return;
    }

    try {
      await mutation.mutateAsync({ relatedEntity: currentEntity, relatedId: currentId, file });
    } catch {
      // Reported by onError.
    }
  }

  return {
    uploadAttachment,
    isUploading: computed(() => mutation.isPending.value)
  };
}
